import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const MapContext = createContext(null);

const LS_LAYERS     = 'ide-wms-layers';
const LS_THEME      = 'ide-theme';
const LS_SIDEBAR    = 'ide-sidebar';
const LS_MUNICIPIO  = 'ide-municipio';
const LS_PERIODO    = 'ide-periodo';

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

export function MapProvider({ children }) {
  const [wmsLayers,          setWmsLayersState]          = useState(() => readLS(LS_LAYERS, []));
  const [theme,              setThemeState]              = useState(() => readLS(LS_THEME, 'light'));
  const [sidebarCollapsed,   setSidebarCollapsedState]   = useState(() => readLS(LS_SIDEBAR, false));
  const [selectedMunicipality, setSelectedMunicipalityState] = useState(() => readLS(LS_MUNICIPIO, null));
  const [selectedPeriod,     setSelectedPeriodState]     = useState(() => readLS(LS_PERIODO, null));
  const [zoomRequest, setZoomRequest] = useState(null);

  // Persist wmsLayers
  useEffect(() => {
    writeLS(LS_LAYERS, wmsLayers);
  }, [wmsLayers]);

  // Persist theme
  useEffect(() => {
    writeLS(LS_THEME, theme);
  }, [theme]);

  // Persist sidebar
  useEffect(() => {
    writeLS(LS_SIDEBAR, sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Persist municipio
  useEffect(() => {
    writeLS(LS_MUNICIPIO, selectedMunicipality);
  }, [selectedMunicipality]);

  // Persist periodo
  useEffect(() => {
    writeLS(LS_PERIODO, selectedPeriod);
  }, [selectedPeriod]);

  // Camadas não são mais auto-adicionadas ao mapa.
  // O usuário navega pelo Catálogo na sidebar e clica para ativar cada camada.

  const addWmsLayer = useCallback((layerData) => {
    const nextId = layerData.id || generateId();

    setWmsLayersState((prev) => {
      const order = prev.length;
      const newLayer = {
        id: nextId,
        name: layerData.name || layerData.layers || 'Camada',
        baseUrl: layerData.baseUrl,
        layers: layerData.layers || '',
        format: layerData.format || 'image/png',
        transparent: layerData.transparent !== undefined ? layerData.transparent : true,
        version: layerData.version || '1.1.1',
        opacity: 1,
        visible: true,
        order,
        metaCache: layerData.metaCache || null,
        cqlFilter: layerData.cqlFilter || null,
      };
      return [...prev, newLayer];
    });

    return nextId;
  }, []);

  const setSelectedMunicipality = useCallback((municipio) => {
    // Ao trocar de município, limpa as camadas ativas (contexto muda completamente)
    setWmsLayersState([]);
    setSelectedMunicipalityState(municipio);
  }, []);

  const setSelectedPeriod = useCallback((periodo) => {
    setSelectedPeriodState(periodo);
  }, []);

  const removeWmsLayer = useCallback((id) => {
    setWmsLayersState((prev) => {
      const filtered = prev.filter((l) => l.id !== id);
      // Recompute order
      return filtered.map((l, i) => ({ ...l, order: i }));
    });
  }, []);

  const removeAllWmsLayers = useCallback(() => {
    setWmsLayersState([]);
  }, []);

  const toggleWmsVisibility = useCallback((id) => {
    setWmsLayersState((prev) =>
      prev.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l))
    );
  }, []);

  const setWmsOpacity = useCallback((id, opacity) => {
    setWmsLayersState((prev) =>
      prev.map((l) => (l.id === id ? { ...l, opacity } : l))
    );
  }, []);

  const reorderWmsLayers = useCallback((newOrderIds) => {
    setWmsLayersState((prev) => {
      const map = new Map(prev.map((l) => [l.id, l]));
      const reordered = newOrderIds
        .map((id, index) => {
          const layer = map.get(id);
          if (!layer) return null;
          return { ...layer, order: index };
        })
        .filter(Boolean);
      // Append any layers not in newOrderIds (safety)
      const reorderedIds = new Set(newOrderIds);
      const remainder = prev
        .filter((l) => !reorderedIds.has(l.id))
        .map((l, i) => ({ ...l, order: reordered.length + i }));
      return [...reordered, ...remainder];
    });
  }, []);

  const updateLayerMetaCache = useCallback((id, metaCache) => {
    setWmsLayersState((prev) =>
      prev.map((l) => (l.id === id ? { ...l, metaCache } : l))
    );
  }, []);

  const requestZoom = useCallback((bbox) => {
    setZoomRequest(bbox);
  }, []);

  const clearZoomRequest = useCallback(() => {
    setZoomRequest(null);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsedState((prev) => !prev);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = {
    wmsLayers,
    theme,
    sidebarCollapsed,
    zoomRequest,
    selectedMunicipality,
    setSelectedMunicipality,
    selectedPeriod,
    setSelectedPeriod,
    addWmsLayer,
    removeWmsLayer,
    removeAllWmsLayers,
    toggleWmsVisibility,
    setWmsOpacity,
    reorderWmsLayers,
    updateLayerMetaCache,
    requestZoom,
    clearZoomRequest,
    toggleSidebar,
    toggleTheme,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
}

export function useMap() {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMap must be used within a MapProvider');
  return ctx;
}

export default MapContext;
